import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { Mail, Phone, MapPin, Instagram, Facebook } from "lucide-react";

const Footer = () => {
  const { t } = useLang();
  const { content } = useSiteContent();
  const year = new Date().getFullYear();

  const cms = content?.contact;
  const address = cms?.address || t.contact.address;
  const phones = cms?.phones?.length ? cms.phones : [{ number: t.contact.phone }];
  const emails = cms?.emails?.length ? cms.emails : [{ address: t.contact.mail }];
  // Social links from CMS (configurable in Admin → Contenuti sito).
  const instagramUrl = cms?.instagram || "https://www.instagram.com/emmanuel_beb/";
  const facebookUrl = cms?.facebook || "https://www.facebook.com/BedandBreakfastEMMANUEL?locale=it_IT";

  return (
    <footer className="bg-[#1E3A5F] text-[#FDFBF7] mt-20" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-12 py-14 grid md:grid-cols-3 gap-10">
        <div>
          <h3 className="font-display text-3xl font-semibold mb-3">B&amp;B Emmanuel</h3>
          <p className="text-sm text-white/70 leading-relaxed max-w-xs">
            {t.footer.tagline}
          </p>
          <div className="flex gap-3 mt-5">
            {instagramUrl && (
              <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full border border-white/20 hover:bg-white/10 transition" data-testid="footer-instagram" aria-label="Instagram">
                <Instagram className="w-4 h-4" />
              </a>
            )}
            {facebookUrl && (
              <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full border border-white/20 hover:bg-white/10 transition" data-testid="footer-facebook" aria-label="Facebook">
                <Facebook className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>

        <div>
          <p className="text-overline text-[#E3A857] mb-4">{t.footer.links}</p>
          <ul className="flex flex-col gap-2 text-sm text-white/80">
            <li><a href="#rooms" className="hover:text-white">{t.nav.rooms}</a></li>
            <li><a href="#services" className="hover:text-white">{t.nav.services}</a></li>
            <li><a href="#attractions" className="hover:text-white">{t.nav.attractions}</a></li>
            <li><Link to="/booking" className="hover:text-white" data-testid="footer-book-link">{t.nav.book}</Link></li>
            <li><Link to="/admin/login" className="hover:text-white text-white/40" data-testid="footer-admin-link">{t.nav.admin}</Link></li>
          </ul>
        </div>

        <div>
          <p className="text-overline text-[#E3A857] mb-4">{t.nav.contact}</p>
          <ul className="flex flex-col gap-3 text-sm text-white/80">
            <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0" />{address}</li>
            {phones.map((p, i) => (
              <li key={`fp-${i}`} className="flex items-start gap-2">
                <Phone className="w-4 h-4 mt-0.5 shrink-0" />
                <a href={`tel:${(p.number || "").replace(/\s+/g, "")}`} className="hover:text-white" data-testid={`footer-phone-${i}`}>{p.number}</a>
              </li>
            ))}
            {emails.map((m, i) => (
              <li key={`fm-${i}`} className="flex items-start gap-2">
                <Mail className="w-4 h-4 mt-0.5 shrink-0" />
                <a href={`mailto:${m.address}`} className="hover:text-white">{m.address}</a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-6">
          <p className="text-overline text-[#E3A857] mb-3 text-center md:text-left">{t.footer.findUsOn || "Trovaci anche su"}</p>
          <div className="flex flex-wrap items-center gap-2.5 justify-center md:justify-start" data-testid="footer-listings">
            <ListingLink
              testId="footer-tripadvisor"
              href="https://www.tripadvisor.com/Hotel_Review-g187777-d1756420-Reviews-B_B_Emmanuel-Reggio_Calabria_Province_of_Reggio_Calabria_Calabria.html"
              brand="tripadvisor"
              label="Tripadvisor"
              rating="3.5 / 5"
            />
            <ListingLink
              testId="footer-booking"
              href="https://www.booking.com/hotel/it/b-reggio-calabria3.it.html"
              brand="bookingdotcom"
              label="Booking.com"
              rating="7.5 / 10"
            />
            <ListingLink
              testId="footer-airbnb"
              href="https://www.airbnb.com/rooms/537772"
              brand="airbnb"
              label="Airbnb"
            />
            <ListingLink
              testId="footer-google"
              href="https://www.google.com/travel/hotels/entity/ChgI-YqRm_mbuNvqARoLL2cvMXRmNF9tNnYQAQ?utm_campaign=sharing&utm_medium=link&utm_source=htls"
              brand="google"
              label="Google Hotels"
            />
            <ListingLink
              testId="footer-trivago"
              href="https://www.trivago.it/it/oar/hotel-b-b-emmanuel-reggio-calabria?search=100-2006983"
              brand="trivago"
              label="Trivago"
            />
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-5 text-xs text-white/50 flex flex-col md:flex-row items-center justify-between gap-2">
          <span>© {year} B&amp;B Emmanuel. {t.footer.rights}</span>
          <span className="text-white/40">CIR: 080063-BBF-00064 · CIN: IT080063C1L94JSVNG</span>
        </div>
      </div>
    </footer>
  );
};

const ListingLink = ({ testId, href, brand, label, rating }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    title={`Apri ${label}${rating ? ` (${rating})` : ""}`}
    className="group inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-md border border-white/15 bg-white/5 hover:bg-white/15 hover:border-white/30 transition"
  >
    <img
      src={`https://cdn.simpleicons.org/${brand}/ffffff`}
      alt={label}
      width="16"
      height="16"
      loading="lazy"
      className="opacity-90 group-hover:opacity-100"
    />
    <span className="text-xs text-white/85 group-hover:text-white whitespace-nowrap">{label}</span>
    {rating && (
      <span className="text-[10px] font-mono text-[#E3A857] bg-white/10 px-1.5 py-0.5 rounded">
        {rating}
      </span>
    )}
  </a>
);

export default Footer;
